/**
 * Status Color Utility
 * 
 * Provides consistent status colors across the application.
 * All colors meet WCAG AA accessibility standards (4.5:1 contrast minimum).
 * 
 * @see STATUS_COLOR_CONSISTENCY_ANALYSIS.md for full documentation
 */

export type StatusType = 
  | 'success' 
  | 'progress' 
  | 'pending' 
  | 'warning' 
  | 'error';

export interface StatusColorClasses {
  /** Full Tailwind classes for badge/pill styling */
  badge: string;
  /** Text color only */
  text: string;
  /** Background color only */
  background: string;
}

/**
 * Maps common status values to their semantic type
 */
export const STATUS_MAPPING: Record<string, StatusType> = {
  // Success states
  'completed': 'success',
  'active': 'success',
  'approved': 'success',
  'on track': 'success',
  'success': 'success',
  'current': 'success',
  
  // Progress states
  'in progress': 'progress',
  'working': 'progress',
  'draft': 'progress',
  
  // Pending states
  'not started': 'pending',
  'pending': 'pending',
  'inactive': 'pending',
  'waiting': 'pending',
  
  // Warning states
  'on hold': 'warning',
  'at risk': 'warning',
  'warning': 'warning',
  'rejected': 'warning',
  
  // Error states
  'cancelled': 'error',
  'closed': 'error',
  'over budget': 'error',
  'critical': 'error',
  'error': 'error',
};

/**
 * Core status color definitions
 * All colors meet WCAG AA standards
 */
export const STATUS_COLORS: Record<StatusType, StatusColorClasses> = {
  success: {
    badge: 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300',
    text: 'text-success-800 dark:text-success-300',
    background: 'bg-success-100 dark:bg-success-900',
  },
  progress: {
    badge: 'bg-info-100 text-info-800 dark:bg-info-900 dark:text-info-300',
    text: 'text-info-800 dark:text-info-300',
    background: 'bg-info-100 dark:bg-info-900',
  },
  pending: {
    badge: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900 dark:text-neutral-300',
    text: 'text-neutral-800 dark:text-neutral-300',
    background: 'bg-neutral-100 dark:bg-neutral-900',
  },
  warning: {
    badge: 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300',
    text: 'text-warning-800 dark:text-warning-300',
    background: 'bg-warning-100 dark:bg-warning-900',
  },
  error: {
    badge: 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300',
    text: 'text-error-800 dark:text-error-300',
    background: 'bg-error-100 dark:bg-error-900',
  },
};

/**
 * Get status color classes for a given status value
 * 
 * @param status - The status string (case-insensitive)
 * @param type - Return type: 'badge' (default), 'text', or 'background'
 * @returns Tailwind CSS classes for the status
 * 
 * @example
 * ```tsx
 * <Badge className={getStatusColor('completed')}>Completed</Badge>
 * <Badge className={getStatusColor('in progress')}>In Progress</Badge>
 * <span className={getStatusColor('on hold', 'text')}>On Hold</span>
 * ```
 */
export function getStatusColor(
  status: string | undefined | null,
  type: 'badge' | 'text' | 'background' = 'badge'
): string {
  if (!status) {
    return STATUS_COLORS.pending[type];
  }

  const normalizedStatus = status.toLowerCase().trim();
  const statusType = STATUS_MAPPING[normalizedStatus] || 'pending';
  
  return STATUS_COLORS[statusType][type];
}

/**
 * Get the semantic status type for a given status value
 * 
 * @param status - The status string (case-insensitive)
 * @returns The semantic status type
 * 
 * @example
 * ```tsx
 * getStatusType('completed') // 'success'
 * getStatusType('in progress') // 'progress'
 * ```
 */
export function getStatusType(status: string | undefined | null): StatusType {
  if (!status) {
    return 'pending';
  }

  const normalizedStatus = status.toLowerCase().trim();
  return STATUS_MAPPING[normalizedStatus] || 'pending';
}

/**
 * Check if a status represents a positive/successful state
 */
export function isSuccessStatus(status: string | undefined | null): boolean {
  return getStatusType(status) === 'success';
}

/**
 * Check if a status represents an active/in-progress state
 */
export function isProgressStatus(status: string | undefined | null): boolean {
  return getStatusType(status) === 'progress';
}

/**
 * Check if a status represents a warning/at-risk state
 */
export function isWarningStatus(status: string | undefined | null): boolean {
  return getStatusType(status) === 'warning';
}

/**
 * Check if a status represents an error/critical state
 */
export function isErrorStatus(status: string | undefined | null): boolean {
  return getStatusType(status) === 'error';
}

/**
 * Legacy color mapping for backward compatibility
 * @deprecated Use getStatusColor() instead
 */
export const LEGACY_STATUS_COLORS: Record<string, string> = {
  'Active': 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300',
  'Completed': 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300',
  'In Progress': 'bg-info-100 text-info-800 dark:bg-info-900 dark:text-info-300',
  'Not Started': 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900 dark:text-neutral-300',
  'On Hold': 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300',
  'Cancelled': 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300',
  'Pending': 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300',
  'Inactive': 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900 dark:text-neutral-300',
  'Closed': 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300',
};
